import { Injectable, NgZone } from '@angular/core';
import { PasswordService } from '@app/services/password/password.service';
import { UserToken } from '@app/services/password/model/user-token';
import { HubConnection, IHttpConnectionOptions } from '@microsoft/signalr';
import { BehaviorSubject, firstValueFrom, Subscription, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SignalRConnectionFactoryService } from '@app/services/signal-r/signal-r-connection-factory.service';

export interface SignalRLoadTestConfig {
  chatHubUrl: string;
  connectionCount: number;
  delayMs: number;
}

export interface SignalRLoadTestState extends SignalRLoadTestConfig {
  isRunning: boolean;
  createdConnections: number;
  scheduledConnections: number;
  connectedConnections: number;
  notConnectedConnections: number;
  reconnectingConnections: number;
  failedConnections: number;
  stoppedConnections: number;
  disconnectedConnections: number;
  completedConnectionAttempts: number;
  reconnectionAttempts: number;
  successfulReconnections: number;
  logEntries: number;
  startedAt: string | null;
  stoppedAt: string | null;
}

type LoadConnectionStatus =
  | 'starting'
  | 'connected'
  | 'reconnecting'
  | 'failed'
  | 'disconnected'
  | 'stopped';

interface LoadTestConnection {
  id: string;
  sequenceNumber: number;
  connection: HubConnection;
  status: LoadConnectionStatus;
  stopRequested: boolean;
  startCompleted: boolean;
  connectionId?: string | null;
}

const defaultChatHubUrl = 'https://backend1-st.faxeit.se/hubs/ChatHub/';
const defaultConnectionCount = 1000;
const defaultDelayMs = 50;

function createInitialState(): SignalRLoadTestState {
  return {
    chatHubUrl: defaultChatHubUrl,
    connectionCount: defaultConnectionCount,
    delayMs: defaultDelayMs,
    isRunning: false,
    createdConnections: 0,
    scheduledConnections: 0,
    connectedConnections: 0,
    notConnectedConnections: 0,
    reconnectingConnections: 0,
    failedConnections: 0,
    stoppedConnections: 0,
    disconnectedConnections: 0,
    completedConnectionAttempts: 0,
    reconnectionAttempts: 0,
    successfulReconnections: 0,
    logEntries: 0,
    startedAt: null,
    stoppedAt: null,
  };
}

@Injectable({
  providedIn: 'root'
})
export class SignalRLoadTestService {
  private readonly stateSubject = new BehaviorSubject<SignalRLoadTestState>(createInitialState());
  readonly state$ = this.stateSubject.asObservable();

  private readonly connections = new Map<string, LoadTestConnection>();
  private readonly logLines: string[] = [];
  private userToken: UserToken | null = null;
  private connectionScheduleSubscription: Subscription | null = null;
  private stopRequested = false;

  constructor(
    private readonly passwordService: PasswordService,
    private readonly ngZone: NgZone,
    private readonly signalRConnectionFactoryService: SignalRConnectionFactoryService
  ) {
    this.passwordService
      .raceUserToken()
      .pipe(filter(userToken => !!userToken))
      .subscribe(userToken => {
        this.userToken = userToken;
      });
  }

  get currentState(): SignalRLoadTestState {
    return this.stateSubject.value;
  }

  async startLoadTest(config: SignalRLoadTestConfig): Promise<void> {
    const normalizedConfig = this.normalizeConfig(config);

    if (!this.userToken?.token) {
      this.userToken = await firstValueFrom(this.passwordService.raceUserToken());
    }

    if (!this.userToken?.token) {
      throw new Error('No ChatHub token is available for the selected race.');
    }

    await this.stopLoadTest(false);
    this.resetState(normalizedConfig);

    if (normalizedConfig.connectionCount === 0) {
      return;
    }

    this.connectionScheduleSubscription = timer(0, normalizedConfig.delayMs)
      .pipe(take(normalizedConfig.connectionCount))
      .subscribe((sequenceNumber: number) => {
        this.createConnection(sequenceNumber + 1, normalizedConfig.chatHubUrl);
      });
  }

  async stopLoadTest(updateStoppedAt = true): Promise<void> {
    this.stopRequested = true;
    this.connectionScheduleSubscription?.unsubscribe();
    this.connectionScheduleSubscription = null;

    const stopPromises = Array.from(this.connections.values()).map(loadConnection => {
      loadConnection.stopRequested = true;
      return loadConnection.connection.stop().catch(error => {
        this.runInZone(() => {
          this.writeLog(loadConnection.id, 'Stop failed', error);
        });
      });
    });

    await Promise.all(stopPromises);

    if (updateStoppedAt && (this.currentState.isRunning || this.connections.size > 0)) {
      this.refreshState({
        isRunning: false,
        stoppedAt: new Date().toISOString(),
      });
      return;
    }

    this.refreshState({
      isRunning: false,
    });
  }

  getLogFileContent(): string {
    return this.logLines.join('\n');
  }

  getLogFileName(): string {
    const timestampSource = this.currentState.startedAt ?? new Date().toISOString();
    return `signalr-load-test-${timestampSource.replace(/[.:]/g, '-')}.txt`;
  }

  async resetStatisticsAndLogs(): Promise<void> {
    await this.stopLoadTest(false);
    this.connectionScheduleSubscription?.unsubscribe();
    this.connectionScheduleSubscription = null;
    this.connections.clear();
    this.logLines.length = 0;
    this.stopRequested = false;
    this.stateSubject.next({
      ...this.currentState,
      isRunning: false,
      createdConnections: 0,
      scheduledConnections: 0,
      connectedConnections: 0,
      notConnectedConnections: 0,
      reconnectingConnections: 0,
      failedConnections: 0,
      stoppedConnections: 0,
      disconnectedConnections: 0,
      completedConnectionAttempts: 0,
      reconnectionAttempts: 0,
      successfulReconnections: 0,
      logEntries: 0,
      startedAt: null,
      stoppedAt: null,
    });
  }

  private createConnection(sequenceNumber: number, chatHubUrl: string): void {
    if (this.stopRequested) {
      return;
    }

    const id = this.toConnectionId(sequenceNumber);
    const options: IHttpConnectionOptions = {
      accessTokenFactory: () => this.userToken?.token ?? '',
    };

    const connection = this.signalRConnectionFactoryService.createConnection(
      chatHubUrl,
      options,
      (msg, data) => this.writeLog(id, msg, data)
    );

    const loadConnection: LoadTestConnection = {
      id,
      sequenceNumber,
      connection,
      status: 'starting',
      stopRequested: false,
      startCompleted: false,
      connectionId: null,
    };

    this.connections.set(id, loadConnection);
    this.registerConnectionCallbacks(loadConnection);
    this.writeLog(id, 'Starting connection', { chatHubUrl, sequenceNumber });
    this.refreshState();

    void connection.start()
      .then(() => {
        this.runInZone(() => {
          loadConnection.startCompleted = true;
          loadConnection.status = 'connected';
          loadConnection.connectionId = connection.connectionId;
          this.writeLog(id, 'Connection established', { connectionId: connection.connectionId });
          this.refreshState();
        });
      })
      .catch(error => {
        this.runInZone(() => {
          loadConnection.startCompleted = true;
          loadConnection.status = 'failed';
          this.writeLog(id, 'Connection start failed', error);
          this.refreshState();
        });
      });
  }

  private registerConnectionCallbacks(loadConnection: LoadTestConnection): void {
    loadConnection.connection.onreconnecting(error => {
      this.runInZone(() => {
        loadConnection.status = 'reconnecting';
        this.writeLog(loadConnection.id, 'Connection reconnecting', error);
        this.refreshState({
          reconnectionAttempts: this.currentState.reconnectionAttempts + 1,
        });
      });
    });

    loadConnection.connection.onreconnected(connectionId => {
      this.runInZone(() => {
        loadConnection.status = 'connected';
        loadConnection.connectionId = connectionId;
        this.writeLog(loadConnection.id, 'Connection reconnected', { connectionId });
        this.refreshState({
          successfulReconnections: this.currentState.successfulReconnections + 1,
        });
      });
    });

    loadConnection.connection.onclose(error => {
      this.runInZone(() => {
        if (loadConnection.stopRequested) {
          loadConnection.status = 'stopped';
          this.writeLog(loadConnection.id, 'Connection stopped', error);
        } else if (loadConnection.status !== 'failed') {
          loadConnection.status = 'disconnected';
          this.writeLog(loadConnection.id, 'Connection closed', error);
        }

        this.refreshState();
      });
    });
  }

  private normalizeConfig(config: SignalRLoadTestConfig): SignalRLoadTestConfig {
    return {
      chatHubUrl: (config.chatHubUrl || defaultChatHubUrl).trim(),
      connectionCount: Math.max(0, Number(config.connectionCount) || 0),
      delayMs: Math.max(0, Number(config.delayMs) || 0),
    };
  }

  private resetState(config: SignalRLoadTestConfig): void {
    this.stopRequested = false;
    this.connections.clear();
    this.logLines.length = 0;
    this.stateSubject.next({
      ...createInitialState(),
      ...config,
      isRunning: true,
      scheduledConnections: config.connectionCount,
      startedAt: new Date().toISOString(),
      stoppedAt: null,
    });
    this.writeLog('test', 'SignalR load test started', config);
  }

  private refreshState(patch: Partial<SignalRLoadTestState> = {}): void {
    const connections = Array.from(this.connections.values());
    const createdConnections = connections.length;
    const connectedConnections = connections.filter(connection => connection.status === 'connected').length;
    const reconnectingConnections = connections.filter(connection => connection.status === 'reconnecting').length;
    const failedConnections = connections.filter(connection => connection.status === 'failed').length;
    const stoppedConnections = connections.filter(connection => connection.status === 'stopped').length;
    const disconnectedConnections = connections.filter(connection => connection.status === 'disconnected').length;
    const completedConnectionAttempts = connections.filter(connection => connection.startCompleted).length;

    this.stateSubject.next({
      ...this.currentState,
      createdConnections,
      scheduledConnections: Math.max(this.currentState.connectionCount - createdConnections, 0),
      connectedConnections,
      notConnectedConnections: createdConnections - connectedConnections,
      reconnectingConnections,
      failedConnections,
      stoppedConnections,
      disconnectedConnections,
      completedConnectionAttempts,
      logEntries: this.logLines.length,
      ...patch,
    });
  }

  private writeLog(source: string, message: string, data?: unknown): void {
    const details = data === undefined ? '' : ` ${this.stringifyLogData(data)}`;
    this.logLines.push(`[${new Date().toISOString()}] [${source}] ${message}${details}`);
    this.refreshState({
      logEntries: this.logLines.length,
    });
  }

  private stringifyLogData(data: unknown): string {
    if (data instanceof Error) {
      return JSON.stringify({
        message: data.message,
        name: data.name,
        stack: data.stack,
      });
    }

    if (typeof data === 'string') {
      return data;
    }

    try {
      return JSON.stringify(data);
    } catch {
      return String(data);
    }
  }

  private runInZone(cb: () => void): void {
    this.ngZone.run(() => cb());
  }

  private toConnectionId(sequenceNumber: number): string {
    return `connection-${sequenceNumber.toString().padStart(4, '0')}`;
  }
}
